// Extracts and formats USDM v4 study-definition facts for SAP generation prompts.
// Mirrors the USDM -> SAP field mapping documented in SAP_Protocol_Ref
// (reference/usdm/USDM-v4-to-SAP-mapping.md), simplified to plain JS objects
// and text since this app's generation is single-shot free text, not the
// claim-verification pipeline that reference repo uses.
//
// A field missing from USDM is simply omitted from the output — never
// fabricated or defaulted to a plausible-looking value.

const decode = (codeLike) => codeLike?.decode || codeLike?.code || codeLike?.name || null;

function getLatestStudyVersion(usdmDoc) {
  const versions = usdmDoc?.study?.versions;
  if (!Array.isArray(versions) || versions.length === 0) return null;
  return versions[versions.length - 1];
}

function getPrimaryStudyDesign(studyVersion) {
  const designs = studyVersion?.studyDesigns;
  if (!Array.isArray(designs) || designs.length === 0) return null;
  return designs[0];
}

// Resolves organization IDs (as used by StudyIdentifier.scopeId,
// StudyRole.organizationIds, etc.) against the studyVersion's organizations
// list. Returns null for any id that doesn't resolve, rather than dropping
// it silently, so callers can tell "no scope given" from "scope given but
// unresolvable."
function resolveOrgName(orgId, organizations) {
  if (!orgId) return null;
  const org = (organizations || []).find((o) => o?.id === orgId);
  return org ? (org.name || org.label || null) : null;
}

function extractIdentity(usdmDoc, studyVersion, projectIdentity, designDescription) {
  const titles = studyVersion?.titles || [];
  const officialTitle = titles.find((t) => /official/i.test(t?.type?.decode || t?.type?.code || ''))?.text || null;
  // "Brief Title" is the USDM v4 controlled-terminology name for this title type, but
  // not every source tags it that way — an uploaded catalogue was seen using "Short"
  // instead (e.g. type.decode "Study Title Short") — so match either.
  const briefTitle = titles.find((t) => /brief|short/i.test(t?.type?.decode || t?.type?.code || ''))?.text || null;

  const organizations = studyVersion?.organizations || [];
  // USDM's StudyIdentifier value is the `text` field, not `studyIdentifier`;
  // the organization that assigned it is a reference (`scopeId`) into
  // studyVersion.organizations, not an inline object.
  const identifiers = (studyVersion?.studyIdentifiers || [])
    .map((id) => {
      const scope = resolveOrgName(id?.scopeId, organizations);
      return id?.text ? `${scope ? `${scope}: ` : ''}${id.text}` : null;
    })
    .filter(Boolean);

  // The USDM record's StudyIdentifier list is derived from the protocol
  // document's own XML header and is legitimately empty whenever that header
  // block wasn't filled in. The trial's Sponsor Protocol Identifier
  // (Project.studyId) is required at trial creation and always available —
  // Protocol's own UI/export already reads it from there instead of the USDM
  // record, so SAP should fall back to the same canonical source rather than
  // going without an identifier the Protocol side always has.
  if (identifiers.length === 0 && projectIdentity?.sponsorProtocolIdentifier) {
    identifiers.push(`Sponsor Protocol Identifier: ${projectIdentity.sponsorProtocolIdentifier}`);
  }

  // Data-quality guard: some USDM exports populate every title `text` with
  // the study code/acronym instead of an actual descriptive title (seen in
  // practice). Flag this rather than silently passing a fake "title"
  // through - callers can fall back to the design description instead.
  const studyName = usdmDoc?.study?.name || null;
  const looksLikeCodeNotTitle = (text) =>
    !text || (studyName && text.trim() === studyName.trim()) || (!text.includes(' ') && text.length < 40);
  const titleUnreliable =
    (!officialTitle && !briefTitle) ||
    (looksLikeCodeNotTitle(officialTitle) && looksLikeCodeNotTitle(briefTitle));

  // Resolve ONE canonical study title HERE — not left for each section's own
  // LLM call to reconcile (same rationale as plannedEnrollmentNumber below).
  // Previously every section was handed officialTitle/briefTitle/studyName as
  // separate, equally-weighted facts with no single answer to "the study
  // title," and only one section's prompt (Document Control) had its own
  // bespoke instruction for resolving the "title is just the code" case -
  // other sections (e.g. 2.1 Study Overview) had no such instruction and
  // picked a different one of those facts, producing a different "Study
  // Title" per section within the same document.
  let resolvedTitle = null;
  let resolvedTitleNote = null;
  if (!titleUnreliable) {
    resolvedTitle = officialTitle || briefTitle;
  } else if (designDescription && designDescription.trim()) {
    resolvedTitle = designDescription.trim();
    resolvedTitleNote = (officialTitle || briefTitle)
      ? 'the source record\'s title field(s) only repeat the study code/name, so the fuller design description is used as the study title instead'
      : 'no descriptive title field was supplied, so the design description is used as the study title instead';
  } else if (officialTitle || briefTitle) {
    resolvedTitle = officialTitle || briefTitle;
    resolvedTitleNote = 'this appears to be the study code/name repeated rather than a descriptive title, and no fuller description was available to substitute';
  }

  return {
    studyName,
    officialTitle: officialTitle || usdmDoc?.study?.label || null,
    briefTitle,
    identifiers,
    titleUnreliable,
    resolvedTitle,
    resolvedTitleNote,
  };
}

function extractSponsor(studyVersion, projectIdentity) {
  const organizations = studyVersion?.organizations || [];
  const roles = studyVersion?.roles || [];
  if (organizations.length === 0) {
    // Same rationale as the identifier fallback above: the USDM record has no
    // organizations whenever the protocol document's XML header sponsor block
    // was left blank, but the trial's registered Sponsor Name is required and
    // always available, and is what Protocol itself displays/exports.
    if (projectIdentity?.sponsorName) {
      return { names: [projectIdentity.sponsorName], confirmed: true, source: 'project' };
    }
    return null;
  }

  const sponsorRoles = roles.filter((r) => /sponsor/i.test(r?.name || r?.code?.decode || ''));
  if (sponsorRoles.length > 0) {
    const names = sponsorRoles
      .flatMap((r) => (r?.organizationIds || []).map((id) => resolveOrgName(id, organizations)))
      .filter(Boolean);
    if (names.length > 0) {
      return { names, confirmed: true };
    }
  }

  // No StudyRole explicitly tagged "Sponsor" (common - many USDM exports,
  // including this app's own mapper, always leave `roles` empty). But an
  // Organization coded with the CDISC controlled term for Sponsor (C70793) is
  // a structural, definitive signal, not a guess: this app's own mapper
  // (ddf/usdm-mapper/mapper.js buildSponsorOrg()) always tags the sponsor
  // organization it creates with exactly this type. Treat it as confirmed as
  // an explicit role tag — leaving it in the "likely" bucket below taught the
  // SAP prompt to hedge on (and often drop) a sponsor name it should have
  // stated directly.
  const isCodedSponsorOrg = (org) => org?.type?.code === 'C70793' || /^sponsor$/i.test(org?.type?.decode || '');
  const codedSponsors = organizations.filter(isCodedSponsorOrg).map((o) => o.name || o.label).filter(Boolean);
  if (codedSponsors.length > 0) {
    return { names: codedSponsors, confirmed: true };
  }

  // No coded/tagged signal at all - fall back to a looser substring guess on
  // the organization type text, but mark this as unconfirmed so the prompt
  // doesn't assert one of them IS the sponsor without evidence.
  const orgTypeIsSponsorLike = (org) => /sponsor|drug company|pharma/i.test(org?.type?.decode || org?.type?.code || '');
  const likelySponsors = organizations.filter(orgTypeIsSponsorLike).map((o) => o.name || o.label).filter(Boolean);
  if (likelySponsors.length > 0) {
    return { names: likelySponsors, confirmed: false };
  }

  const allOrgNames = organizations.map((o) => o.name || o.label).filter(Boolean);
  if (allOrgNames.length > 0) {
    return { names: allOrgNames, confirmed: false, noRoleInfo: true };
  }
  if (projectIdentity?.sponsorName) {
    return { names: [projectIdentity.sponsorName], confirmed: true, source: 'project' };
  }
  return null;
}

function extractDesign(design) {
  if (!design) return null;
  return {
    label: design.label || null,
    description: design.description || null,
    rationale: design.rationale || null,
    studyType: decode(design.studyType),
    studyPhase: decode(design.studyPhase?.standardCode) || decode(design.studyPhase),
    model: decode(design.model),
    subTypes: (design.subTypes || []).map(decode).filter(Boolean),
    intentTypes: (design.intentTypes || []).map(decode).filter(Boolean),
    blindingSchema: decode(design.blindingSchema),
  };
}

function extractArmsAndEpochs(design) {
  const arms = (design?.arms || []).map((arm) => ({
    name: arm?.name || null,
    type: decode(arm?.type),
    description: arm?.description || null,
  }));
  const epochs = (design?.epochs || []).map((epoch) => epoch?.name).filter(Boolean);
  const interventions = (design?.studyInterventions || []).map((iv) => ({
    name: iv?.name || null,
    role: decode(iv?.role),
    type: decode(iv?.type),
  }));
  return { arms, epochs, interventions };
}

function extractObjectivesAndEndpoints(design) {
  const objectives = design?.objectives || [];
  return objectives.map((objective) => ({
    level: decode(objective?.level),
    text: objective?.text || objective?.description || null,
    endpoints: (objective?.endpoints || []).map((ep) => ({
      level: decode(ep?.level),
      text: ep?.text || ep?.description || null,
      id: ep?.id || null,
    })),
  }));
}

function extractEstimands(design, endpointsById) {
  const estimands = design?.estimands || [];
  return estimands.map((estimand) => ({
    variableOfInterest: endpointsById.get(estimand?.variableOfInterestId) || estimand?.variableOfInterestId || null,
    analysisPopulation: estimand?.analysisPopulationId || null,
    summaryMeasure: estimand?.summaryMeasure || null,
    intercurrentEvents: (estimand?.intercurrentEvents || []).map((ice) => ({
      name: ice?.name || null,
      strategy: decode(ice?.strategy) || ice?.strategy || null,
    })),
  }));
}

function extractAnalysisPopulations(design) {
  return (design?.analysisPopulations || []).map((pop) => ({
    id: pop?.id || null,
    name: pop?.name || pop?.label || null,
    text: pop?.text || pop?.description || null,
  }));
}

function extractPopulation(design) {
  const population = design?.population;
  if (!population) return null;
  return {
    // USDM models these as a Quantity/Range object ({ value, unit }), not a plain
    // number — usdmCatalogueContext.js (protocol side) already unwraps .value for
    // this exact reason. This file didn't, so plannedEnrollmentNumber ended up
    // being the raw object here, and `${population.plannedEnrollmentNumber}` in
    // buildContextText() below stringified it to "[object Object]" instead of the
    // actual sample size — the source of the SAP-vs-protocol discrepancy for
    // enrollment/sample-size figures. The `?? population.plannedEnrollmentNumber`
    // fallback keeps this working if a raw number ever comes through instead.
    plannedEnrollmentNumber: population.plannedEnrollmentNumber?.value ?? population.plannedEnrollmentNumber ?? null,
    plannedCompletionNumber: population.plannedCompletionNumber?.value ?? population.plannedCompletionNumber ?? null,
    plannedSex: (population.plannedSex || []).map(decode).filter(Boolean),
    plannedAge: population.plannedMinimumAgeOfSubjects?.text || population.plannedMaximumAgeOfSubjects?.text || null,
  };
}

// Conservative fallback for when the structured plannedEnrollmentNumber field
// is empty but the statistical methods narrative states the resulting total
// in prose (e.g. "...a total of 672 participants will be enrolled and
// randomized (336 participants per arm)"). Deliberately narrow patterns only —
// this exists so Section 2.7 and Section 3.7 read the SAME resolved number
// from the SAME place, rather than asking the LLM to independently reconcile
// two sources for two separately-generated sections (Section 2 is generated
// before Section 3 even exists yet, so it can't "stay consistent" with it on
// its own — that instruction was never reliably followable, which is why 2.7
// kept saying "not specified" while 3.7 correctly had the real number).
function extractEnrollmentTotalFromNarrative(narrativeText) {
  if (!narrativeText) return null;
  const patterns = [
    /(?:a\s+total\s+of\s+)?(\d[\d,]{1,7})\s+(?:participants|patients|subjects)\s+will\s+be\s+enroll(?:ed)?/i,
    /(?:sample\s+size\s+of|enroll(?:ing)?\s+approximately)\s+(\d[\d,]{1,7})\s+(?:participants|patients|subjects)/i,
  ];
  for (const re of patterns) {
    const m = narrativeText.match(re);
    if (m) {
      const n = Number(m[1].replace(/,/g, ''));
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  return null;
}

function extractReferenceInfo(usdmDoc, studyVersion) {
  const protocolVersion = studyVersion?.versionIdentifier || null;
  const rawDocs = [
    ...(Array.isArray(usdmDoc?.study?.documentedBy) ? usdmDoc.study.documentedBy : (usdmDoc?.study?.documentedBy ? [usdmDoc.study.documentedBy] : [])),
    ...(Array.isArray(studyVersion?.referenceIdentifiers) ? studyVersion.referenceIdentifiers : []),
  ];
  const referenceDocuments = rawDocs
    .map((doc) => doc?.name || doc?.label || doc?.title || doc?.referenceIdentifier || null)
    .filter(Boolean);
  if (!protocolVersion && referenceDocuments.length === 0) return null;
  return { protocolVersion, referenceDocuments };
}

// Protocol side (protocol/utils/usdmCatalogueContext.js) already surfaces this
// same narrativeContentItems entry to the protocol generator as
// `statisticalMethodsNarrative` — this file never read it at all, so any
// sponsor-authored statistical methods narrative captured in the USDM record
// (beyond the bare enrollment number) silently never reached SAP generation.
function extractStatisticalMethodsNarrative(studyVersion) {
  const items = studyVersion?.narrativeContentItems || [];
  const match = items.find((n) => String(n?.name || '').toLowerCase() === 'statistical methods');
  return match?.text || null;
}

function extractSchedule(design) {
  const encounters = design?.encounters || [];
  const activities = design?.activities || [];
  if (encounters.length === 0 && activities.length === 0) return null;
  return {
    encounterCount: encounters.length,
    encounterNames: encounters.map((e) => e?.name).filter(Boolean).slice(0, 20),
    activityCount: activities.length,
  };
}

/**
 * Extracts a plain-JS-object summary of the USDM facts relevant to SAP
 * generation. Any category absent from the source document is simply
 * omitted from the returned object.
 * @param {object} usdmDoc - the raw USDM v4 document (the `usdm` field
 *   returned by GET /api/protocol-registration/usdm/:projectId)
 * @param {{sponsorProtocolIdentifier?: string, sponsorName?: string}} [projectIdentity] -
 *   the trial's canonical identity (from projectIdentityFields.js), used as a
 *   fallback only when the USDM record itself has no studyIdentifiers/organizations
 *   (see extractIdentity/extractSponsor) — never overrides data the USDM record has.
 */
export function extractUsdmFacts(usdmDoc, projectIdentity) {
  if (!usdmDoc || typeof usdmDoc !== 'object') return null;

  const studyVersion = getLatestStudyVersion(usdmDoc);
  const design = getPrimaryStudyDesign(studyVersion);

  const objectives = design ? extractObjectivesAndEndpoints(design) : [];
  const endpointsById = new Map();
  objectives.forEach((obj) => {
    obj.endpoints.forEach((ep) => {
      if (ep.id) endpointsById.set(ep.id, ep.text);
    });
  });

  const population = design ? extractPopulation(design) : null;
  const statisticalMethodsNarrative = extractStatisticalMethodsNarrative(studyVersion);

  // Resolve enrollment ONCE here — not left for each section's own LLM call to
  // reconcile — so every section that reads `population.plannedEnrollmentNumber`
  // (2.7, 3.7, ...) gets the identical value from the identical source.
  if (population && population.plannedEnrollmentNumber == null) {
    const fromNarrative = extractEnrollmentTotalFromNarrative(statisticalMethodsNarrative);
    if (fromNarrative != null) {
      population.plannedEnrollmentNumber = fromNarrative;
      population.plannedEnrollmentSource = 'statistical methods narrative (not a separate structured field)';
    }
  }

  return {
    identity: extractIdentity(usdmDoc, studyVersion, projectIdentity, design?.description),
    sponsor: extractSponsor(studyVersion, projectIdentity),
    referenceInfo: extractReferenceInfo(usdmDoc, studyVersion),
    design: design ? extractDesign(design) : null,
    armsAndEpochs: design ? extractArmsAndEpochs(design) : null,
    objectives,
    estimands: design ? extractEstimands(design, endpointsById) : [],
    analysisPopulations: design ? extractAnalysisPopulations(design) : [],
    population,
    schedule: design ? extractSchedule(design) : null,
    statisticalMethodsNarrative,
  };
}

/**
 * Renders extracted USDM facts into a labeled plain-text block suitable for
 * injection into an LLM prompt as `usdmContext`.
 * @param {ReturnType<typeof extractUsdmFacts>} facts
 */
export function formatUsdmFactsForPrompt(facts) {
  if (!facts) return '';
  const lines = [];

  const { identity, sponsor, referenceInfo, design, armsAndEpochs, objectives, estimands, analysisPopulations, population, schedule, statisticalMethodsNarrative } = facts;

  if (identity && (identity.resolvedTitle || identity.studyName || identity.officialTitle || identity.identifiers.length)) {
    lines.push('STUDY IDENTITY');
    if (identity.resolvedTitle) {
      lines.push(
        `- Study title (this is the resolved, authoritative study title — use this EXACT text as "the study title"/"Study Title" in EVERY section that names it, e.g. both Document Control and Section 2.1; do not substitute the study name/code or independently pick a different title field): ${identity.resolvedTitle}`
      );
      if (identity.resolvedTitleNote) lines.push(`  (note: ${identity.resolvedTitleNote})`);
    }
    if (identity.studyName) lines.push(`- Study name/code: ${identity.studyName}`);
    if (identity.identifiers.length) lines.push(`- Identifiers (protocol/registry numbers): ${identity.identifiers.join('; ')}`);
    lines.push('');
  }

  if (sponsor && sponsor.names.length) {
    lines.push('SPONSOR');
    if (sponsor.source === 'project') {
      lines.push(`- Sponsor (from the trial's registered Sponsor Name; the USDM study record itself lists no organizations): ${sponsor.names.join('; ')}`);
    } else if (sponsor.confirmed) {
      lines.push(`- Sponsor (explicitly tagged in the source record): ${sponsor.names.join('; ')}`);
    } else if (sponsor.noRoleInfo) {
      lines.push(`- No organization is explicitly tagged as "Sponsor" in the source record. Organizations named in the record: ${sponsor.names.join('; ')}. Do not assert one of these is the sponsor without confirming - ask or note as an open question.`);
    } else {
      lines.push(`- Likely sponsor (organization type suggests it, but no explicit "Sponsor" role tag in the source record): ${sponsor.names.join('; ')}. Flag as unconfirmed if used.`);
    }
    lines.push('');
  }

  if (referenceInfo && (referenceInfo.protocolVersion || referenceInfo.referenceDocuments.length)) {
    lines.push('PROTOCOL VERSION & REFERENCE DOCUMENTS (for Section 0.4 and Section 12 — do not invent a version number or document not listed here)');
    if (referenceInfo.protocolVersion) lines.push(`- Protocol version this study record was generated from: ${referenceInfo.protocolVersion}`);
    if (referenceInfo.referenceDocuments.length) lines.push(`- Reference documents named in the source record: ${referenceInfo.referenceDocuments.join('; ')}`);
    lines.push('');
  }

  if (design) {
    lines.push('STUDY DESIGN');
    if (design.studyPhase) lines.push(`- Phase: ${design.studyPhase}`);
    if (design.studyType) lines.push(`- Study type: ${design.studyType}`);
    if (design.model) lines.push(`- Design model: ${design.model}`);
    if (design.subTypes.length) lines.push(`- Sub-types: ${design.subTypes.join(', ')}`);
    if (design.intentTypes.length) lines.push(`- Intent types: ${design.intentTypes.join(', ')}`);
    if (design.blindingSchema) lines.push(`- Blinding: ${design.blindingSchema}`);
    if (design.description) lines.push(`- Description (verbatim from the source record): ${design.description}`);
    if (design.rationale) lines.push(`- Design rationale (verbatim from the source record): ${design.rationale}`);
    lines.push('');
  }

  if (armsAndEpochs && (armsAndEpochs.arms.length || armsAndEpochs.epochs.length || armsAndEpochs.interventions.length)) {
    lines.push('ARMS, EPOCHS & INTERVENTIONS');
    armsAndEpochs.arms.forEach((arm) => {
      lines.push(`- Arm: ${arm.name || '(unnamed)'}${arm.type ? ` [${arm.type}]` : ''}${arm.description ? ` — ${arm.description}` : ''}`);
    });
    if (armsAndEpochs.epochs.length) lines.push(`- Epochs (in order): ${armsAndEpochs.epochs.join(' -> ')}`);
    armsAndEpochs.interventions.forEach((iv) => {
      lines.push(`- Intervention: ${iv.name || '(unnamed)'}${iv.role ? ` [role: ${iv.role}]` : ''}${iv.type ? ` [type: ${iv.type}]` : ''}`);
    });
    lines.push('');
  }

  if (objectives.length) {
    lines.push('OBJECTIVES & ENDPOINTS');
    objectives.forEach((obj, i) => {
      lines.push(`- Objective ${i + 1} [${obj.level || 'level unspecified'}]: ${obj.text || '(no text provided)'}`);
      obj.endpoints.forEach((ep) => {
        lines.push(`    - Endpoint [${ep.level || 'level unspecified'}]: ${ep.text || '(no text provided)'}`);
      });
    });
    lines.push('');
  }

  if (estimands.length) {
    lines.push('ESTIMANDS & INTERCURRENT EVENTS (the source record does not always populate every attribute — treat missing attributes as gaps, not zero/none)');
    estimands.forEach((est, i) => {
      lines.push(`- Estimand ${i + 1}:`);
      lines.push(`    - Variable of interest: ${est.variableOfInterest || '(not specified in the source record)'}`);
      lines.push(`    - Analysis population reference: ${est.analysisPopulation || '(not specified in the source record)'}`);
      lines.push(`    - Population-level summary measure: ${est.summaryMeasure || '(not specified in the source record)'}`);
      if (est.intercurrentEvents.length) {
        est.intercurrentEvents.forEach((ice) => {
          lines.push(`    - Intercurrent event: ${ice.name || '(unnamed)'} — strategy: ${ice.strategy || '(not specified in the source record)'}`);
        });
      } else {
        lines.push('    - Intercurrent events: none listed in the source record');
      }
    });
    lines.push('');
  }

  if (analysisPopulations.length) {
    lines.push('ANALYSIS POPULATIONS (names only, as defined in the source record — it does NOT provide inclusion/exclusion rules for these; the SAP must define who is actually in each population)');
    analysisPopulations.forEach((pop) => {
      lines.push(`- ${pop.name || pop.id || '(unnamed population)'}${pop.text ? `: ${pop.text}` : ''}`);
    });
    lines.push('');
  }

  if (population && (population.plannedEnrollmentNumber != null || population.plannedCompletionNumber != null)) {
    lines.push('PLANNED ENROLLMENT (this is the resolved, authoritative total — use this exact number in EVERY section that reports planned sample size, e.g. both Section 2.7 and Section 3.7; do not independently re-derive or re-check the narrative below for a different figure)');
    if (population.plannedEnrollmentNumber != null) {
      lines.push(
        `- Planned enrollment: ${population.plannedEnrollmentNumber}${
          population.plannedEnrollmentSource ? ` (source: ${population.plannedEnrollmentSource})` : ''
        }`
      );
    }
    if (population.plannedCompletionNumber != null) lines.push(`- Planned completion: ${population.plannedCompletionNumber}`);
    if (population.plannedSex.length) lines.push(`- Planned sex: ${population.plannedSex.join(', ')}`);
    if (population.plannedAge) lines.push(`- Planned age range: ${population.plannedAge}`);
    lines.push('');
  }

  if (statisticalMethodsNarrative) {
    lines.push('STATISTICAL METHODS NARRATIVE (from the source record)');
    lines.push(statisticalMethodsNarrative);
    lines.push('');
  }

  if (schedule) {
    lines.push('SCHEDULE OF ASSESSMENTS (reference only — do not attempt to reproduce the full schedule grid from this count)');
    lines.push(`- ${schedule.encounterCount} encounter(s) defined${schedule.encounterNames.length ? `: ${schedule.encounterNames.join(', ')}` : ''}`);
    lines.push(`- ${schedule.activityCount} activity/activities defined`);
    lines.push('');
  }

  return lines.join('\n').trim();
}
